import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Upload } from "lucide-react";
import api, { formatError, fileUrl } from "@/api";
import { toast } from "sonner";

export default function AddPatientModal({ doctors, onCreated, defaultDoctor }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    doctor_id: defaultDoctor || "",
    name: "", age: "", mobile: "", photo: "", source: "counter", gender: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo: data.path }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/patients", {
        ...form,
        age: parseInt(form.age),
        photo: form.photo || undefined,
        gender: form.gender || undefined,
      });
      toast.success("Patient added · MOCKED SMS sent");
      setOpen(false);
      setForm({ doctor_id: defaultDoctor || "", name: "", age: "", mobile: "", photo: "", source: "counter", gender: "" });
      onCreated?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="add-patient-trigger">
          <UserPlus className="w-4 h-4" /> Add Patient
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Patient to Queue</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg border-2 border-dashed bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.photo ? <img src={fileUrl(form.photo)} alt="" className="w-full h-full object-cover" /> : <Upload className="w-5 h-5 text-slate-400" />}
            </div>
            <label className="text-sm text-sky-600 hover:underline cursor-pointer" data-testid="add-patient-photo">
              {uploading ? "Uploading..." : "Upload patient photo (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
          </div>

          {doctors && (
            <div>
              <Label>Doctor</Label>
              <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
                <SelectTrigger data-testid="add-patient-doctor"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialization_en}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Patient Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-patient-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Age</Label>
              <Input type="number" min="0" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} required data-testid="add-patient-age" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} required minLength={10} maxLength={10} data-testid="add-patient-mobile" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Gender <span className="text-slate-400 font-normal">(optional)</span></Label>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { v: "male", label: "Male" },
                { v: "female", label: "Female" },
                { v: "other", label: "Other" },
              ].map((g) => (
                <button type="button" key={g.v}
                  onClick={() => setForm({ ...form, gender: form.gender === g.v ? "" : g.v })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${form.gender === g.v
                    ? "bg-sky-500 text-white border-sky-500"
                    : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"}`}
                  data-testid={`add-patient-gender-${g.v}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-lg border p-2">
            <label className="text-xs text-slate-500 px-2">Source:</label>
            {["counter", "walk_in", "phone"].map((s) => (
              <button type="button" key={s} onClick={() => setForm({ ...form, source: s })}
                className={`text-xs px-3 py-1 rounded-full ${form.source === s ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`}
                data-testid={`source-${s}`}>
                {s === "walk_in" ? "Walk-In" : s === "phone" ? "Phone" : "Counter"}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting || !form.doctor_id} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-patient-submit">
              {submitting ? "Adding..." : "Add to Queue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
